import { supabase } from "@/lib/supabase";
import type { IBoardService } from "@/services/interfaces/IBoardService";
import { AlreadyPinnedError } from "@/services/errors";
import {
  isBoardColumn, normalizeDraft, normalizePatch, pinTitle, positionBetween, validateDraft, validatePatch,
  type BoardCard, type BoardCardDraft, type BoardCardPatch, type BoardColumn, type BoardLink, type LinkedTarget,
  type PinTarget,
} from "@/lib/board";
import type { ContentI18n } from "@/lib/questContent/types";

interface EmbedRow {
  id: string;
  title: string;
  status: string;
  i18n: ContentI18n | null;
  source_lang: string | null;
}

export interface BoardRow {
  id: string;
  column: string;
  position: number;
  title: string;
  notes: string | null;
  due_date: string | null;
  created_at: string;
  quest_id: string | null;
  task_id: string | null;
  link_kind: string | null;
  link_id: string | null;
  quest: EmbedRow | null;
  task: EmbedRow | null;
}

// One query for the whole board: the badges ride on the embeds. Embeds go
// through quests/tasks RLS, so an item the owner can no longer see comes back
// as null and the card falls back to its stored title. link_kind/link_id say
// what was pinned even after the item is deleted (the FK goes NULL, they don't).
export const BOARD_SELECT =
  "id,column,position,title,notes,due_date,created_at,quest_id,task_id,link_kind,link_id," +
  "quest:quests(id,title,status,i18n,source_lang),task:tasks(id,title,status,i18n,source_lang)";

function toTarget(e: EmbedRow | null): LinkedTarget | null {
  return e ? { title: e.title, status: e.status, i18n: e.i18n ?? null, sourceLang: e.source_lang ?? null } : null;
}

function toLink(row: BoardRow): BoardLink | null {
  if (!row.link_id) return null;
  if (row.link_kind === "quest") return { kind: "quest", id: row.link_id, target: toTarget(row.quest) };
  if (row.link_kind === "task") return { kind: "task", id: row.link_id, target: toTarget(row.task) };
  return null;
}

export function mapBoardRow(row: BoardRow): BoardCard {
  const link = toLink(row);
  return {
    id: row.id,
    column: isBoardColumn(row.column) ? row.column : "backlog",
    position: Number(row.position),
    title: row.title,
    notes: row.notes,
    dueDate: row.due_date,
    createdAt: row.created_at,
    link,
  };
}

function fail(error: { message: string }): never {
  throw new Error(error.message);
}

// Same contract as the mock: a card that's gone (deleted in another tab) is
// "not found", not a connection problem or a silent no-op.
const NOT_FOUND = "board.errors.notFound";

/** RLS scopes this to the caller's own cards, so no owner filter is needed. */
async function bottomPosition(column: BoardColumn): Promise<number> {
  const { data, error } = await supabase.from("board_cards").select("position")
    .eq("column", column).order("position", { ascending: false }).limit(1).maybeSingle();
  // Guessing on a failed read would put the card at the top of the column.
  if (error) fail(error);
  return positionBetween((data as { position: number } | null)?.position, undefined);
}

export class SupabaseBoardService implements IBoardService {
  async list(): Promise<BoardCard[]> {
    const { data, error } = await supabase.from("board_cards").select(BOARD_SELECT);
    if (error) fail(error);
    return ((data ?? []) as unknown as BoardRow[]).map(mapBoardRow);
  }

  async create(draft: BoardCardDraft): Promise<BoardCard> {
    const invalid = validateDraft(draft);
    if (invalid) throw new Error(invalid);
    const d = normalizeDraft(draft);
    const position = await bottomPosition(d.column);
    const { data, error } = await supabase.from("board_cards")
      .insert({ column: d.column, position, title: d.title, notes: d.notes, due_date: d.dueDate })
      .select(BOARD_SELECT).single();
    if (error) fail(error);
    return mapBoardRow(data as unknown as BoardRow);
  }

  async update(id: string, patch: BoardCardPatch): Promise<BoardCard> {
    const invalid = validatePatch(patch);
    if (invalid) throw new Error(invalid);
    const p = normalizePatch(patch);
    const row: Record<string, unknown> = {};
    if (p.title !== undefined) row.title = p.title;
    if (p.notes !== undefined) row.notes = p.notes;
    if (p.dueDate !== undefined) row.due_date = p.dueDate;
    const { data, error } = await supabase.from("board_cards").update(row).eq("id", id).select(BOARD_SELECT).single();
    if (error?.code === "PGRST116") throw new Error(NOT_FOUND);
    if (error) fail(error);
    return mapBoardRow(data as unknown as BoardRow);
  }

  async move(id: string, column: BoardColumn, index?: number): Promise<void> {
    // One transaction: place the card and renumber its column (SECURITY
    // INVOKER, so board_cards RLS still applies). P0002 = not the caller's card.
    const { error } = await supabase.rpc("board_move_card", {
      p_card_id: id, p_column: column, p_index: index ?? null,
    });
    if (error?.code === "P0002") throw new Error(NOT_FOUND);
    if (error) fail(error);
  }

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from("board_cards").delete().eq("id", id);
    if (error) fail(error);
  }

  async pin(target: PinTarget): Promise<BoardCard> {
    const position = await bottomPosition("todo");
    const title = pinTitle(target);
    const { data, error } = await supabase.from("board_cards")
      .insert({
        column: "todo", position, title,
        [target.kind === "quest" ? "quest_id" : "task_id"]: target.id,
      })
      .select(BOARD_SELECT).single();
    if (error?.code === "23505") throw new AlreadyPinnedError(target.kind, target.id);
    if (error) fail(error);
    return mapBoardRow(data as unknown as BoardRow);
  }
}
